import React, { useEffect, useState } from "react";
import { NETWORK_LABEL } from "../contexts/config";
import { useLocation, useNavigate } from "react-router-dom";
import { useActiveAccount, useActiveWalletChain } from "thirdweb/react";
import { getLpPairAddress } from "../services/apiService";
import { balanceOf, getCurrencyMetadata } from "thirdweb/extensions/erc20";
import { getErc20Contract } from "../contexts/thirdWebClient";
import { useConnectWallet } from "../hooks/useConnectWallet";
import { readContract } from "thirdweb";
import { ethers } from "ethers";

const Pool = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const add = useActiveAccount();
    const [address, setAddress] = useState("");
    const chain = useActiveWalletChain();
    const [loading, setLoading] = useState(false);
    const { openConnect, isConnecting } = useConnectWallet();

    useEffect(() => {
        if (add && add.address && chain) {
            setAddress(add.address);
            getLpListOfUser(add.address, chain);
        } else if (!add?.address) {
            // Positions belong to the account that was connected — drop them
            // rather than leaving another wallet's list on screen.
            setAddress("");
            setLpDetails([]);
        }
    }, [add, chain]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh data when navigating back with refresh state
    useEffect(() => {
        if (location.state?.refresh && add && add.address && chain) {
            getLpListOfUser(add.address, chain);
            // Clear the state to prevent re-fetching on subsequent renders
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    const [, setLpAddress] = useState([]);

    const getLpListOfUser = async (address, chain) => {
        setLoading(true);
        const trxData = await getLpPairAddress(address);
        //console.log(trxData,'data')
        if (trxData.status) {
            console.log("%c Line:14 🍯 trxData", "color:#4fff4B", trxData);
            setLpAddress(trxData.data);
            fetchLpDetails(trxData.data, address, chain);
        } else {
            setLoading(false);
        }
    };

    const [lpDetails, setLpDetails] = useState([]);

    const fetchLpDetails = async (trxData, address, chain) => {
        let myLp = [];
        for (const element of trxData) {
            try {
                const contract = getErc20Contract(element.pairAddress, chain);
                let currencyMetadata = await getCurrencyMetadata({ contract });
                let pairTotalSupply = await readContract({
                    contract,
                    method: "function totalSupply() view returns (uint256)",
                    params: [],
                });
                let userBalance = await balanceOf({
                    contract,
                    address: address,
                });

                const Token0contract = getErc20Contract(
                    element?.liquiditypairs?.token0Address,
                    chain,
                );

                let tokenOMetaData = await getCurrencyMetadata({
                    contract: Token0contract,
                });
                let tokenOUserBalance = await balanceOf({
                    contract: Token0contract,
                    address: element.pairAddress,
                });

                const Token1contract = getErc20Contract(
                    element?.liquiditypairs?.token1Address,
                    chain,
                );
                let token1MetaData = await getCurrencyMetadata({
                    contract: Token1contract,
                });
                let token1UserBalance = await balanceOf({
                    contract: Token1contract,
                    address: element.pairAddress,
                });

                let userShare =
                    (Number(
                        ethers.formatUnits(
                            userBalance,
                            currencyMetadata?.decimals,
                        ),
                    ) *
                        100) /
                    Number(
                        ethers.formatUnits(
                            pairTotalSupply,
                            currencyMetadata?.decimals,
                        ),
                    );

                let pairData = {
                    pairAddress: element.pairAddress,
                    token0Address: element?.liquiditypairs?.token0Address,
                    token1Address: element?.liquiditypairs?.token1Address,
                    lpName:
                        tokenOMetaData?.symbol +
                        "-" +
                        token1MetaData?.symbol +
                        " LP",
                    lpTotalSupply: Number(
                        ethers.formatUnits(
                            pairTotalSupply,
                            currencyMetadata?.decimals,
                        ),
                    ),
                    lpUserBalance: Number(
                        ethers.formatUnits(
                            userBalance,
                            currencyMetadata?.decimals,
                        ),
                    ),
                    userShare: userShare,
                    token0Name: tokenOMetaData?.name,
                    token0Symbol: tokenOMetaData?.symbol,
                    token0UserBalance:
                        (userShare *
                            Number(
                                ethers.formatUnits(
                                    tokenOUserBalance,
                                    tokenOMetaData?.decimals,
                                ),
                            )) /
                        100,
                    token1Name: token1MetaData?.name,
                    token1Symbol: token1MetaData?.symbol,
                    token1UserBalance:
                        (userShare *
                            Number(
                                ethers.formatUnits(
                                    token1UserBalance,
                                    token1MetaData?.decimals,
                                ),
                            )) /
                        100,
                };
                console.log(
                    "%c Line:85 🍰 pairData",
                    "color:#6ec1c2",
                    pairData,
                );
                myLp.push(pairData);
            } catch (error) {
                console.log("%c Line:47 🍉 error for pair:", "color:#42b983", element.pairAddress, error);
                console.log("element data:", JSON.stringify(element, null, 2));
            }
        }
        console.log("%c Line:148 🍒 myLp", "color:#ea7e5c", myLp);
        setLpDetails(myLp);
        setLoading(false);
    };

    const positionCount = lpDetails?.length || 0;

    return (
        <div className="w-full max-w-[1000px] grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)] gap-4 lg:gap-5 items-start">

            {/* ── Positions ─────────────────────────────────────────────── */}
            <div className="w-full flex gap-3 flex-col card-3d p-4 sm:p-6">
                <div className="flex items-center justify-between gap-3 w-full">
                    <h2 className="text-[20px] sm:text-[22px] font-bold gradient-text">
                        Positions
                    </h2>
                    <button
                        onClick={() => navigate("/pool/addliquidity/0x/0x")}
                        className="btn-3d py-2.5 px-4 sm:px-5 text-[12px] sm:text-[13px] font-bold shrink-0"
                    >
                        + New Position
                    </button>
                </div>

                {loading ? (
                    <div className="inner-3d flex flex-col items-center gap-4 justify-center w-full min-h-[220px]">
                        <div className="animate-pulse text-usdx-ink-subtle text-[14px] font-medium">
                            Loading positions…
                        </div>
                    </div>
                ) : positionCount > 0 ? (
                    lpDetails.map((ele) => (
                        <div key={ele?.pairAddress} className="pool-card-3d overflow-hidden w-full">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 gap-2">
                                <h3 className="text-usdx-ink font-bold text-[15px] sm:text-[17px] truncate">
                                    {ele?.lpName}
                                </h3>
                                <div className="flex gap-2 shrink-0">
                                    <button
                                        onClick={() => navigate(`/pool/addliquidity/${ele?.token0Address}/${ele?.token1Address}`)}
                                        className="token-pill-3d py-1.5 px-4 text-usdx-accent text-[12px] font-bold"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => navigate(`/remove/liquidity/${ele?.pairAddress}`)}
                                        className="token-pill-3d py-1.5 px-4 text-usdx-ink-muted text-[12px] font-bold"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 py-3 sm:py-4 gap-1 sm:gap-2 border-t border-usdx-line mx-3 sm:mx-4">
                                <div className="flex flex-col items-center gap-1">
                                    <p className="num font-bold text-[13px] sm:text-[15px] text-usdx-ink text-center">
                                        {(ele?.token0UserBalance).toFixed(4)}
                                    </p>
                                    <p className="text-[11px] text-usdx-ink-subtle font-medium truncate">{ele?.token0Symbol}</p>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <p className="num font-bold text-[13px] sm:text-[15px] text-usdx-ink text-center">
                                        {(ele?.token1UserBalance).toFixed(4)}
                                    </p>
                                    <p className="text-[11px] text-usdx-ink-subtle font-medium truncate">{ele?.token1Symbol}</p>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <p className="num font-bold text-[13px] sm:text-[15px] text-usdx-accent">
                                        {ele?.userShare.toFixed(2)}%
                                    </p>
                                    <p className="text-[11px] text-usdx-ink-subtle font-medium">Pool share</p>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="inner-3d flex flex-col items-center gap-3 justify-center w-full min-h-[220px] px-6 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-usdx-accent/10 flex items-center justify-center mb-1">
                            <svg
                                className="w-7 h-7 text-usdx-accent"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                {/* Two overlapping coins — the pair a position is made of */}
                                <circle cx="9" cy="9" r="5.25" strokeWidth={1.75} />
                                <circle
                                    cx="15"
                                    cy="15"
                                    r="5.25"
                                    strokeWidth={1.75}
                                    className="opacity-55"
                                />
                            </svg>
                        </div>
                        {/* Without an account there is nothing to have read, so
                            claiming the list is empty would be a guess. */}
                        <p className="text-[14px] text-usdx-ink font-semibold">
                            {address
                                ? "No liquidity positions yet"
                                : "Connect your wallet to see your positions"}
                        </p>
                        <p className="text-[12px] text-usdx-ink-subtle max-w-[280px] leading-relaxed">
                            {address
                                ? "Add liquidity to a pair and it will show up here with your share and underlying balances."
                                : "Your positions are read from the connected account, along with your share of each pool."}
                        </p>
                        {!address && (
                            <button
                                className="btn-3d mt-1 py-2.5 px-5 text-[13px] font-bold"
                                disabled={isConnecting}
                                onClick={openConnect}
                            >
                                {isConnecting ? "Connecting…" : "Connect wallet"}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Overview ──────────────────────────────────────────────────
                Sits beside the list on desktop, below it on mobile. */}
            <aside className="w-full flex flex-col gap-4">
                <div className="card-3d p-4 sm:p-5">
                    <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mb-1">
                        Overview
                    </h3>
                    <div className="flex flex-col">
                        {[
                            { label: "Positions", value: loading ? "…" : String(positionCount) },
                            { label: "Protocol", value: "AMM V2" },
                            { label: "Swap fee", value: "0.30%" },
                            { label: "Network", value: NETWORK_LABEL },
                        ].map((row) => (
                            <div
                                key={row.label}
                                className="flex items-baseline justify-between gap-3 py-2.5 border-b border-usdx-line-soft last:border-b-0"
                            >
                                <span className="text-[12px] text-usdx-ink-subtle font-medium">
                                    {row.label}
                                </span>
                                <span className="num text-[12px] font-semibold text-usdx-ink">
                                    {row.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

            </aside>

            {/* ── How it works ───────────────────────────────────────────────
                Spans both columns beneath the list and the overview panel. */}
            <section className="lg:col-span-2 w-full card-3d p-4 sm:p-6">
                <h3 className="text-[12px] font-semibold text-usdx-ink-subtle uppercase tracking-widest mb-4 text-left">
                    How it works
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                        {
                            step: "1",
                            title: "Open a pool",
                            desc: "Create a pool for any token pair, or add to one that already exists. No listing process and no approval needed.",
                        },
                        {
                            step: "2",
                            title: "Deposit the pair",
                            desc: "Supply both tokens in the pool's current ratio. You receive LP tokens representing your share of it.",
                        },
                        {
                            step: "3",
                            title: "Earn and withdraw",
                            desc: "Collect a share of the 0.30% fee on every swap routed through the pool. Withdraw part or all of it whenever you want.",
                        },
                    ].map((item) => (
                        <div key={item.step} className="pool-card-3d p-4 flex flex-col gap-2 text-left">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-usdx-accent/10 text-usdx-accent text-[12px] font-bold shrink-0">
                                {item.step}
                            </div>
                            <p className="text-[13px] font-bold text-usdx-ink">{item.title}</p>
                            <p className="text-[11px] text-usdx-ink-subtle leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {[
                        { title: "No lock-up period", desc: "Positions are never frozen — add or remove liquidity at any time." },
                        { title: "Any token pair", desc: "Pools are permissionless. If the pair does not exist yet, creating it is the first deposit." },
                    ].map((item) => (
                        <div key={item.title} className="pool-card-3d p-4 flex flex-col gap-1.5 text-left">
                            <p className="text-[12px] font-bold text-usdx-ink">{item.title}</p>
                            <p className="text-[11px] text-usdx-ink-subtle leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Pool;
